import { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import FundingFilters from '../utils/FundingFilters';
import FundingItem from './RNAI/FundingItem';
import styles from './RNAI/Form.module.css';

const REGIONS = [
  'Global / International',
  'United States',
  'Canada',
  'Europe',
  'Asia',
  'Africa',
  'Oceania',
  'South America',
];

export default function RefreshFiltersModal({
  open,
  onClose,
  onConfirm,
  busy = false,
  showFunding,
  showRegion,
  initialOpportunityStatus = ['open', 'forecasted'],
  initialFundingFilters = {},
  initialRegion = 'United States',
}) {
  const [includeOpenGrants, setIncludeOpenGrants] = useState(true);
  const [includeForecastGrants, setIncludeForecastGrants] = useState(true);
  const [includeClosedGrants, setIncludeClosedGrants] = useState(false);
  const [fundingFilterValues, setFundingFilterValues] = useState({});
  const [fundingFilterEnabled, setFundingFilterEnabled] = useState({});
  const [selectedRegion, setSelectedRegion] = useState('United States');

  useEffect(() => {
    if (!open) return;
    const status = Array.isArray(initialOpportunityStatus) ? initialOpportunityStatus : [];
    setIncludeOpenGrants(status.includes('open'));
    setIncludeForecastGrants(status.includes('forecasted'));
    setIncludeClosedGrants(status.includes('closed'));
    const values = initialFundingFilters || {};
    setFundingFilterValues(values);
    setFundingFilterEnabled(
      Object.keys(values).reduce((acc, k) => ({ ...acc, [k]: true }), {})
    );
    setSelectedRegion(initialRegion || 'United States');
  }, [open]);

  const updateFundingFilter = (key, value) => {
    setFundingFilterValues((prev) => ({ ...prev, [key]: value }));
    setFundingFilterEnabled((prev) => ({ ...prev, [key]: true }));
  };

  const toggleFilter = (key, enabled) => {
    setFundingFilterEnabled((prev) => ({ ...prev, [key]: enabled }));
  };

  const handleConfirm = () => {
    const result = {};
    if (showFunding) {
      result.OpportunityStatus = [
        includeOpenGrants && 'open',
        includeForecastGrants && 'forecasted',
        includeClosedGrants && 'closed',
      ].filter(Boolean);
      result.fundingFilters = Object.keys(fundingFilterValues).reduce((acc, key) => {
        if (fundingFilterEnabled[key]) {
          acc[key] = fundingFilterValues[key];
        }
        return acc;
      }, {});
    }
    if (showRegion) {
      result.region = selectedRegion;
    }
    onConfirm(result);
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '10px' } }}
    >
      <DialogContent sx={{ textAlign: 'left', paddingTop: '20px !important' }}>
        {showFunding && (
          <div className={styles.funding_filters} style={{ marginBottom: '16px' }}>
            <div className={styles.filtersHeader}>
              <div className={styles.boldText}>Funding Filters</div>
            </div>
            <div className={styles.filtersList}>
              <div className={styles.row}>
                <input
                  type="checkbox"
                  checked={includeOpenGrants}
                  onChange={(e) => setIncludeOpenGrants(e.target.checked)}
                />
                <label className={styles.label}>Include Open Grants</label>
              </div>
              <div className={`${styles.row} ${styles.checkboxRow}`}>
                <input
                  type="checkbox"
                  checked={includeForecastGrants}
                  onChange={(e) => setIncludeForecastGrants(e.target.checked)}
                />
                <label className={styles.label}>Include Forecast Grants</label>
              </div>
              <div className={`${styles.row} ${styles.checkboxRow}`}>
                <input
                  type="checkbox"
                  checked={includeClosedGrants}
                  onChange={(e) => setIncludeClosedGrants(e.target.checked)}
                />
                <label className={styles.label}>Include Closed Grants</label>
              </div>

              {FundingFilters.map((item) => (
                <FundingItem
                  key={item.heading}
                  item={item}
                  fundingFilters={fundingFilterValues}
                  updateFilters={updateFundingFilter}
                  enabledMap={fundingFilterEnabled}
                  toggleFilter={toggleFilter}
                />
              ))}
            </div>
          </div>
        )}

        {showRegion && (
          <div className={styles.funding_filters}>
            <div className={styles.filtersHeader}>
              <div className={styles.boldText}>Organizations/Jobs/Events Filters</div>
            </div>
            <p className={styles.label} style={{ fontWeight: 'bold', marginLeft: '10px' }}>
              Geographic Region
            </p>
            <div className={styles.filtersList}>
              {REGIONS.map((region) => (
                <div className={`${styles.row} ${styles.checkboxRow}`} key={region}>
                  <input
                    type="checkbox"
                    checked={selectedRegion === region}
                    onChange={() => setSelectedRegion(region)}
                  />
                  <label className={styles.label}>{region}</label>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', padding: '20px 24px 24px' }}>
        <Button
          variant="contained"
          onClick={onClose}
          disabled={busy}
          sx={{
            backgroundColor: 'gray',
            '&:hover': { backgroundColor: '#666666', boxShadow: 'none' },
          }}
        >
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={busy}>
          Run Search
        </Button>
      </DialogActions>
    </Dialog>
  );
}
